import { create } from '@bufbuild/protobuf'
import { timestampDate, timestampFromDate } from '@bufbuild/protobuf/wkt'
import { addSeconds, subSeconds } from 'date-fns'
import {
    DateTimeOffsetDto,
    DateTimeOffsetDtoSchema,
} from '@/generated/Messages/Common_pb'
import { assertNotNullish } from './assertNotNullish'

/**
 * Transforms the passed `Date` into a `DateTimeOffsetDto` object.
 * This also automatically includes the timezone offset of the passed `Date`.
 * @param date to be transformed.
 * @returns the `Date` as a `DateTimeOffsetDto`.
 */
export const dateToDateTimeOffsetDto = (date: Date) => {
    // Sign needs to be inverted, because `getTimezoneOffset` returns a negative value, when the backend needs a positive value and the other way around.
    // https://web.archive.org/web/20250212142105/https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getTimezoneOffset#negative_values_and_positive_values
    const offset = date.getTimezoneOffset() * -60

    return create(DateTimeOffsetDtoSchema, {
        timeZoneOffset: {
            nanos: 0,
            seconds: BigInt(offset),
        },
        // add seconds so the time can be interpreted correct when reading a DateTimeOffsetDto from the api
        timestamp: timestampFromDate(addSeconds(date, offset)),
    })
}

/**
 * Transforms the passed `DateTimeOfffsetDto` into a `Date`.
 *
 * @param dto to be transformed.
 * @returns the `DateTimeOffset` as a `Date` with the `timeZoneOffset` subtracted to have the correct timezone again.
 * @throws if either `timeZoneOffset` or `timestamp` is `undefined` or `null`.
 */
export const dateTimeOffsetDtoToDate = ({
    timeZoneOffset,
    timestamp,
}: DateTimeOffsetDto): Date => {
    assertNotNullish(timeZoneOffset)
    assertNotNullish(timestamp)

    const date = timestampDate(timestamp)

    return subSeconds(date, Number(timeZoneOffset.seconds))
}
